import React, { createContext, useContext, useEffect } from "react";

import { Route, Routes, useNavigate } from "react-router-dom";

import Courses from "../Courses";
import Overview from "../Overview";
import SingleCourse from "../SingleCourse";

import { AllFatherContext } from "../../App";
import { AppContextProps } from "../../Context/AppContext";
import { Endpoints } from "../../Lib/endpoints";
import { NonPaginatedResponse } from "../../Lib/responses";
import {
  PlanProps,
  RequiredStripeCustomerProps,
  RequiredStripePaymentMethodProps,
  StudentProps,
} from "../../Lib/types";
import { usePerformRequest } from "../../Lib/usePerformRequest";

import TopNav from "../../Components/TopNav";
import { HttpSuccessCodesObj } from "../../Lib/data";
import Account from "../Account";
import CourseAssessments from "../CourseAssessments";
import CoursePerformance from "../CoursePerformance";
import CourseRecordings from "../CourseRecordings";
import LessonMaterials from "../LessonMaterials";
import PurchasePlan from "../PurchasePlan";
import SearchPage from "../Search";
import SingleAssessment from "../SingleAssessment";
import SingleLessonLiveKit from "../SingleLessonLiveKit";
import SingleRecording from "../SingleRecording";
import "./styles.scss";
export const AppContext = createContext<AppContextProps | undefined>(undefined);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const allFatherContext = useContext(AllFatherContext);

  const {
    data: studentProfile,
    response: studentProfileResponse,
    reloadData: reloadStudentProfile,
    isLoading: isLoadingProfile,
  } = usePerformRequest<NonPaginatedResponse<StudentProps>>({
    method: Endpoints.GetStudentProfile().method,
    route: Endpoints.GetStudentProfile().route,
  });
  const {
    data: plans,
    isLoading: isLoadingPlans,
    reloadData: reloadPlans,
  } = usePerformRequest<NonPaginatedResponse<PlanProps[]>>({
    method: Endpoints.GetPlans(allFatherContext?.locationInformation?.currency).method,
    route: Endpoints.GetPlans(allFatherContext?.locationInformation?.currency).route,
  });

  const {
    data: stripeProfile,
    response: stripeProfileResponse,
    reloadData: reloadStripeProfile,
    isLoading: isLoadingStripeProfile,
  } = usePerformRequest<
    NonPaginatedResponse<{
      stripeProfile: RequiredStripeCustomerProps;
      paymentMethod: RequiredStripePaymentMethodProps;
    }>
  >({
    method: Endpoints.GetStudentStripeCustomer().method,
    route: Endpoints.GetStudentStripeCustomer().route,
  });

  useEffect(() => {
    if (studentProfileResponse && studentProfileResponse.status) {
      if (studentProfileResponse.status !== HttpSuccessCodesObj.Success) {
        navigate("/login");
      }
    }
  }, [isLoadingProfile, studentProfileResponse]);

  // Tab close warning effect
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Custom message for the warning
      const message = "Are you sure you want to leave? Your progress may be lost.";
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    // Add event listener for tab close
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const LogoutAdmin = async () => {
    localStorage.removeItem("t");
    navigate("/login");
  };

  // Use theme from global context
  const { theme, setTheme } = allFatherContext || {
    theme: "light",
    setTheme: () => {},
  };

  return (
    <AppContext.Provider
      value={{
        logout: LogoutAdmin,
        profile: studentProfile?.data,
        stripeProfile: stripeProfile?.data?.stripeProfile,
        paymentMethod: stripeProfile?.data?.paymentMethod,
        isLoadingStripeProfile,
        reloadStripeProfile,
        isLoadingProfile,
        reloadProfile: reloadStudentProfile,
        theme,

        setTheme,
        plans: plans?.data,
        reloadPlans: reloadPlans,
        isLoadingPlans,
      }}
    >
      <div className="dashboard-container">
        <TopNav />
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/overview" element={<Overview />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/course/:courseID" element={<SingleCourse />} />
          <Route path="/lesson/:lessonID" element={<SingleLessonLiveKit />} />
          <Route path="/materials/:lessonID" element={<LessonMaterials />} />
          <Route path="/performance/:courseID" element={<CoursePerformance />} />

          <Route path="/assessments/:lessonID" element={<CourseAssessments />} />
          <Route path="/assessment/:assessmentID" element={<SingleAssessment />} />
          <Route path="/purchase-plan/:planID" element={<PurchasePlan />} />

          <Route path="/account" element={<Account />} />
          <Route path="/recordings/:courseID" element={<CourseRecordings />} />
          <Route path="/recording/:recordingID" element={<SingleRecording />} />
          <Route path="/search" element={<SearchPage />} />
        </Routes>
      </div>
    </AppContext.Provider>
  );
};

export default Dashboard;
